<template>
  <div class="preview-container">
    <div
      v-if="items.length === 0"
      class="empty-state"
    >
      <p class="empty-title">No {{ modelStore.modelName }}s added yet</p>
      <p class="empty-subtitle">Add {{ modelStore.modelName }}s using the form or bulk upload on the left.</p>
    </div>

    <div v-else class="preview-content">
      <!-- Header -->
      <div class="preview-header">
        <div class="preview-header-left">
          <template v-if="editMode">
            <input
              :checked="allSelected"
              type="checkbox"
              class="checkbox"
              @change="toggleGroupSelect"
            >
            <span class="text-xs text-gray-500">{{ allSelected ? "Deselect All" : "Select All" }}</span>
          </template>
          <span v-else class="text-sm font-semibold text-gray-700">{{ items.length }} {{ items.length === 1 ? modelStore.modelName : modelStore.modelName + 's' }} ready to upload</span>
        </div>
        <div class="preview-header-right">
          <button
            v-if="editMode && hasSelectedItems"
            class="cursor-pointer hover:bg-red-100 rounded-full p-1.5 inline-flex items-center justify-center transition-colors"
            @click="confirmingRemove = true"
          >
            <icon
              icon="trash"
              color="#dc2626"
              size="18px"
            />
          </button>
          <button
            class="text-xs font-medium px-3 py-1.5 rounded transition-colors"
            :class="editMode ? 'border border-red-500 text-red-500 hover:bg-red-500 hover:text-white' : 'border border-transparent bg-default_primary_dark hover:bg-default_primary_darkest text-default_white'"
            @click="toggleEditMode"
          >
            {{ editMode ? 'Done' : 'Edit' }}
          </button>
        </div>
      </div>

      <!-- Table -->
      <div class="table-wrapper">
        <table class="preview-table">
          <thead>
            <tr>
              <th v-if="editMode" class="th-checkbox"></th>
              <th
                v-for="col in columns"
                :key="col.key"
              >
                {{ col.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in items"
              :key="index"
              :class="{ 'row-checked': editMode && item.checked }"
            >
              <td v-if="editMode" class="td-checkbox">
                <input
                  v-model="item.checked"
                  type="checkbox"
                  class="checkbox"
                >
              </td>
              <td
                v-for="col in columns"
                :key="col.key"
              >
                {{ formatValue(item[col.key], col.type) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Confirm Remove Modal -->
    <base-modal
      v-if="confirmingRemove"
      title="Remove Items"
      size="small"
      @close="confirmingRemove = false"
    >
      <div class="mt-2">
        Are you sure you want to remove {{ selectedCount }} {{ selectedCount > 1 ? modelStore.modelName + 's' : modelStore.modelName }} from the list?
      </div>
      <div class="mt-2 text-sm text-gray-500">
        This cannot be undone.
      </div>
      <div class="flex flex-row justify-between mt-4">
        <button
          class="btn btn-outline w-1/2 flex justify-center"
          @click="confirmingRemove = false"
        >
          Cancel
        </button>
        <button
          class="btn btn-filled btn-warning ml-2 w-1/2 flex justify-center"
          @click="confirmRemove"
        >
          Remove
        </button>
      </div>
    </base-modal>
  </div>
</template>

<script>

// Renders records in a table from the excel importer. Allows deletion of rows from the table
import {useI18n} from "vue-i18n";
import Icon from "../Icon.vue";
import BaseModal from "../BaseModal.vue";
import {ApplicationHelper} from "../../../helpers/ApplicationHelper";

export default {
  components: {Icon, BaseModal},
  props: {
    modelStore: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const {t} = useI18n();
    return {t};
  },
  data() {
    return {
      editMode: false,
      confirmingRemove: false,
      excludedFields: ['checked', 'uploaded', 'notes'],
    };
  },
  computed: {
    items() {
      return this.modelStore.tempRecords;
    },
    columns() {
      const template = this.modelStore.modelTemplate;
      return Object.keys(template)
        .filter(key => !this.excludedFields.includes(key))
        .map(key => ({
          key,
          label: this.formatLabel(key),
          type: template[key].type
        }));
    },
    allSelected() {
      return this.items.length > 0 && this.items.every(item => item.checked === true);
    },
    hasSelectedItems() {
      return this.items.some(item => item.checked === true);
    },
    selectedCount() {
      return this.items.filter(item => item.checked === true).length;
    },
  },
  methods: {
    formatLabel(key) {
      const labelMap = {
        f_name: 'First Name',
        l_name: 'Last Name',
        email: 'Email',
        amount: 'Amount',
        designation: 'Designation',
        paymentType: 'Method',
        date: 'Date',
        role: 'Role',
        phone: 'Phone',
        name: 'Name'
      };
      if (labelMap[key]) return labelMap[key];
      // Fallback: convert camelCase/snake_case to Title Case
      return key
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, c => c.toUpperCase());
    },
    formatValue(val, type) {
      if (val === null || val === undefined || val === '') return '—';
      if (type === 'money') return ApplicationHelper.formatMoney(val);
      if (type === 'date') return ApplicationHelper.formatDate(val, { time: false, weekday: false });
      return val;
    },
    toggleEditMode() {
      this.editMode = !this.editMode;
      if (!this.editMode) {
        this.items.forEach(item => { item.checked = false; });
      }
    },
    selectAll() {
      this.items.forEach(item => { item.checked = true; });
    },
    unselectAll() {
      this.items.forEach(item => { item.checked = false; });
    },
    toggleGroupSelect(event) {
      if (event.target.checked) {
        this.selectAll();
      } else {
        this.unselectAll();
      }
    },
    removeSelectedRecords() {
      // eslint-disable-next-line vue/no-mutating-props
      this.modelStore.tempRecords = this.modelStore.tempRecords.filter(item => !item.checked);
      this.confirmingRemove = false;
      this.editMode = false;
    },
    confirmRemove() {
      this.removeSelectedRecords();
    }
  }
};
</script>

<style scoped lang="scss">
.preview-container {
  @apply h-full flex flex-col;
}

.empty-state {
  @apply flex flex-col items-center justify-center h-full text-center px-4;
}

.empty-title {
  @apply text-gray-500 font-medium text-sm;
}

.empty-subtitle {
  @apply text-gray-400 text-xs mt-1;
}

.preview-content {
  @apply flex flex-col h-full;
}

.preview-header {
  @apply flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 sticky top-0 z-10;
}

.preview-header-left {
  @apply flex items-center gap-2;
}

.preview-header-right {
  @apply flex items-center gap-2;
}

.table-wrapper {
  @apply flex-1 overflow-y-auto;
}

.preview-table {
  @apply w-full text-sm;

  thead {
    @apply sticky top-0 bg-white;

    tr {
      @apply border-b border-gray-200;
    }

    th {
      @apply px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide;
    }
  }

  tbody {
    tr {
      @apply border-b border-gray-100 transition-colors;

      &:hover {
        @apply bg-gray-50;
      }

      &.row-checked {
        @apply bg-red-50;
      }
    }

    td {
      @apply px-3 py-2.5 text-sm text-gray-800;
    }
  }
}

.th-checkbox,
.td-checkbox {
  @apply w-8 px-2;
}

.checkbox {
  @apply w-4 h-4 rounded border-gray-300 text-default_primary_dark focus:ring-default_primary_dark cursor-pointer;
}

@media (max-width: 1400px) {
  .preview-table {
    thead th {
      @apply px-2 py-1.5 text-[10px];
    }

    tbody td {
      @apply px-2 py-2 text-xs;
    }
  }

  .preview-header {
    @apply px-3 py-2;
  }
}

@media (max-width: 768px){
  .preview-table{
    @apply text-xs
  }
}
</style>
